import { createFileRoute } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Layout } from "@/components/Layout";
import { ContactForm } from "@/components/contact/ContactForm";
import { ContactFaqSection } from "@/components/contact/ContactFaqSection";
import simonMaiPhoto from "@/assets/simon-mai.webp.asset.json";
import { OfficeMarquee } from "@/components/kontakt/OfficeMarquee";
import { HQ_PHONE_DISPLAY, HQ_PHONE_TEL } from "@/config/contact";

// Optional statt .default(""): Mit Standardwert hängt der Router leere
// Parameter an und leitet /kontakt per 307 auf /kontakt?anliegen=&mentoring=
// um. Der Canonical /kontakt zeigte dadurch auf eine Weiterleitung (Ahrefs,
// 07.10.2026).
const searchSchema = z.object({
  anliegen: fallback(z.string(), "").optional(),
  // Ticket ME1: Herkunft aus einer Mentoring-Seite.
  mentoring: fallback(z.string(), "").optional(),
});

const CONTACT_FAQS: Array<{ q: string; a: string }> = [
  { q: "Was kostet das Erstgespräch?", a: "Nichts. Das Erstgespräch ist kostenlos und unverbindlich. Sie gehen damit keinerlei Verpflichtung ein." },
  { q: "Wie schnell bekomme ich eine Antwort?", a: "Innerhalb von 24 Stunden. Meistens schneller." },
  { q: "Muss ich schon wissen, was ich brauche?", a: "Nein. Genau dafür ist das Erstgespräch da. Sie schildern Ihre Situation, wir zeigen Ihnen die Möglichkeiten." },
  { q: "Mit wem spreche ich?", a: "Mit Simon Mai, Geschäftsführer von creategy. Kein Callcenter, kein Vertriebsteam. Der Mensch, mit dem Sie sprechen, ist auch der, der Ihr Projekt versteht." },
  { q: "Wie kurzfristig kann creategy starten?", a: "Sehr kurzfristig. Unser Express-Setup ermöglicht den Kampagnenstart in sieben Tagen. Auch wenn Ihr Wahltermin näher rückt, lohnt sich das Gespräch." },
];

const OFFICES = [
  { name: "creategy Berlin", street: "Danziger Straße 6", postalCode: "10435", locality: "Berlin" },
  { name: "creategy Stockach", street: "Heinrich-Bettinger Str. 14", postalCode: "78333", locality: "Stockach" },
  { name: "creategy Rhein-Main", street: "Hainstädter Straße 29", postalCode: "63512", locality: "Hainburg" },
];

export const Route = createFileRoute("/kontakt/")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Kontakt | creategy" },
      {
        name: "description",
        content:
          "Ob Bürgermeisterwahl, Personalratswahl oder Verbandskommunikation – am Anfang steht ein Gespräch. Kostenlos, unverbindlich, Antwort binnen 24 Stunden.",
      },
      { property: "og:title", content: "Kontakt | creategy" },
      {
        property: "og:description",
        content:
          "Ob Bürgermeisterwahl, Personalratswahl oder Verbandskommunikation – am Anfang steht ein Gespräch. Kostenlos, unverbindlich, Antwort binnen 24 Stunden.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://creategy.de/kontakt" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Kontakt | creategy" },
      {
        name: "twitter:description",
        content:
          "Ob Bürgermeisterwahl, Personalratswahl oder Verbandskommunikation – am Anfang steht ein Gespräch. Kostenlos, unverbindlich, Antwort binnen 24 Stunden.",
      },
    ],
    links: [{ rel: "canonical", href: "https://creategy.de/kontakt" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: CONTACT_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      ...OFFICES.map((o, i) => ({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": `https://creategy.de/kontakt#office-${i}`,
          name: o.name,
          parentOrganization: { "@type": "Organization", name: "creategy GmbH", url: "https://creategy.de" },
          url: "https://creategy.de/kontakt",
          telephone: "+49-30-82686000",
          email: "hello@creategy.de",
          address: {
            "@type": "PostalAddress",
            streetAddress: o.street,
            postalCode: o.postalCode,
            addressLocality: o.locality,
            addressCountry: "DE",
          },
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "09:00",
              closes: "18:00",
            },
          ],
        }),
      })),
    ],
  }),
  component: ContactPage,
});

type Location = {
  city: string;
  description: string;
  street: string;
  zip: string;
};

const LOCATIONS: Location[] = [
  {
    city: "Berlin",
    description:
      "Unser Hauptquartier. Dort, wo politische Kommunikation gemacht wird, wollen wir sein.",
    street: "Danziger Straße 6",
    zip: "10435 Berlin",
  },
  {
    city: "Baden-Württemberg",
    description:
      "Unser Gründungsstandort. Nah an den Gemeinden, nah an unseren Wurzeln.",
    street: "Heinrich-Bettinger Str. 14",
    zip: "78333 Stockach",
  },
  {
    city: "Rhein-Main",
    description:
      "Unser jüngster Standort. Weil politische Kommunikation bundesweite Präsenz braucht.",
    street: "Hainstädter Straße 29",
    zip: "63512 Hainburg",
  },
];

type Step = { n: string; h: string; t: string };
const STEPS: Step[] = [
  {
    n: "01",
    h: "Wir melden uns.",
    t: "Innerhalb von 24 Stunden, per Mail oder Telefon. Je nachdem, was Sie bevorzugen.",
  },
  {
    n: "02",
    h: "Wir hören zu.",
    t: "Im kostenlosen Erstgespräch geht es um Ihre Situation, Ihre Ziele und Ihren Zeitplan. Nicht um einen Pitch.",
  },
  {
    n: "03",
    h: "Sie entscheiden.",
    t: "Nach dem Gespräch erhalten Sie ein maßgeschneidertes Angebot. Ob und wie es weitergeht, bestimmen Sie. Ohne Druck.",
  },
];

const HQ_EMAIL = "hello@creategy.de";

function ContactPage() {
  const { anliegen = "", mentoring = "" } = Route.useSearch();
  return (
    <Layout>
      {/* Sektion 1: Hero */}
      <section>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <h1 className="heading-h1 text-primary">Kommen Sie mit uns in den Kontakt.</h1>
          <div className="mt-8 grid gap-10 md:mt-10 md:grid-cols-[1fr_3fr] md:items-center md:gap-16">

            {/* Foto Simon Mai */}
            <div className="fade-in-up">
              <div className="relative aspect-square w-full max-w-xs overflow-hidden">
                <img
                  src={simonMaiPhoto.url}
                  alt="Simon Mai, Geschäftsführer creategy"
                  className="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
            {/* Text */}
            <div>
              <p className="max-w-2xl font-body text-base leading-relaxed text-foreground md:text-lg">
                Ob Wahlkampf, Personalratswahl oder Verbandskommunikation: Am Anfang
                steht immer ein Gespräch. Kostenlos, unverbindlich und ohne Umwege.
                Ihr Ansprechpartner ist Simon Mai, Geschäftsführer von creategy.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Sektion 2: Zweispaltiger Kontaktbereich */}
      <section aria-labelledby="kontakt-form">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-12 lg:grid-cols-[55fr_45fr] lg:gap-16 lg:items-start">
            {/* Links: Formular */}
            <div>
              <h2 id="kontakt-form" className="heading-h2 text-primary">
                Schreiben Sie uns.
              </h2>
              <div className="mt-10">
                <ContactForm defaultTopic={anliegen} mentoringSlug={mentoring} />
              </div>
            </div>

            {/* Rechts: Ablauf + Direktkontakt */}
            <div className="space-y-12">
              <div>
                <h2 className="heading-h2 text-primary">
                  Was nach Ihrer{" "}
                  Nachricht{" "}
                  passiert.
                </h2>
                <ol className="mt-10 space-y-8">
                  {STEPS.map((s) => (
                    <li key={s.n} className="flex gap-5">
                      <span className="font-display text-2xl font-extrabold leading-none text-primary md:text-3xl">
                        {s.n}
                      </span>
                      <div>
                        <h3 className="font-display text-lg font-extrabold text-primary">
                          {s.h}
                        </h3>
                        <p className="mt-2 font-body text-base leading-relaxed text-foreground">
                          {s.t}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-sm bg-muted p-8">
                <h3 className="font-display text-lg font-extrabold text-primary">
                  Lieber direkt?
                </h3>
                <div className="mt-4 space-y-2 font-body text-base text-foreground">
                  <p>
                    Telefon:{" "}
                    <a
                      href={`tel:${HQ_PHONE_TEL}`}
                      className="border-b border-primary text-primary transition-opacity hover:opacity-80"
                    >
                      {HQ_PHONE_DISPLAY}
                    </a>
                  </p>
                  <p>
                    E-Mail:{" "}
                    <a
                      href={`mailto:${HQ_EMAIL}`}
                      className="border-b border-primary text-primary transition-opacity hover:opacity-80"
                    >
                      {HQ_EMAIL}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 3: Standorte */}
      <section aria-labelledby="standorte" className="bg-muted">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <h2 id="standorte" className="heading-h2 text-primary">
              Dreimal{" "}
              creategy.
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {LOCATIONS.map((loc) => (
              <div
                key={loc.city}
                className="flex h-full flex-col rounded-sm bg-background p-8"
              >
                <h3 className="heading-h3 text-primary">{loc.city}</h3>
                <p className="mt-4 font-body text-base leading-relaxed text-foreground">
                  {loc.description}
                </p>
                <address className="mt-4 font-body text-sm not-italic leading-relaxed text-foreground/70">
                  {loc.street}
                  <br />
                  {loc.zip}
                </address>
              </div>
            ))}
          </div>

          <p className="mx-auto mt-12 max-w-2xl text-center font-body text-base leading-relaxed text-foreground md:text-lg">
            Für ein Gespür vor Ort kommen wir übrigens gerne direkt zu Ihnen. In
            die Gemeinde, in die Geschäftsstelle oder ins Rathaus.
          </p>
        </div>
      </section>

      {/* Bilderlauf */}
      <OfficeMarquee />


      {/* Sektion 4: FAQ */}
      <ContactFaqSection />
    </Layout>
  );
}
